import * as React from "react";
import PageContent from "../../../layout/PageContent";
import {Currency, OrderSettingsFragment, useOrderSettingsQuery, useOrderSettingsUpdateMutation} from "../../../Queries";
import {useNavigate, useParams} from "react-router-dom";
import {Button, LinearProgress} from "@mui/material";
import {FormProvider, useForm} from "react-hook-form";
import {TitleText} from "../../../layout/Typography";
import {SavingUI} from "../../../SavingProvider";
import SettingsHeader from "../SettingsHeader";
import {ShopTabs} from "../ShopTabs";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {FormInputCheckbox} from "../../shop/wholesale/form/FormInputCheckbox";
import {FormInputPrice} from "../../shop/wholesale/form/FormInputPrice";
import {FormInputNumber} from "../../shop/wholesale/form/FormInputNumber";
import {FormInputJSON} from "../../shop/wholesale/form/FormInputJSON";

const FORMID_ORDERS = "SettingsOrders";

export const OrderSettingsPath = (brandId: string, shopId?: string) => {
   let path = "/brands/" + brandId + "/settings/orderset";
   if (shopId) {
      path += '/' + shopId
   }
   return path;
}

export const OrderSettings = () => {
   const {brandHouseId, shopId} = useParams<{ brandHouseId: string, shopId: string }>();
   const navigate = useNavigate();

   const handleChange = (newValue: string) => {
      navigate(OrderSettingsPath(brandHouseId, newValue));
   };

   return <div>
      <SettingsHeader title={"Orders"} subtitle={"Settings for orders"}>
         <Button type="submit" form={FORMID_ORDERS} variant={"contained"}>Save</Button>
      </SettingsHeader>

      <ShopTabs
         activeId={shopId}
         brandHouseId={brandHouseId}
         onChange={(value) => handleChange(value)}
         renderDefault={() => <OrdersInner brandHouseId={brandHouseId}/>}
         renderShop={(shop) => <OrdersInner shopId={shop}/>}
      />
   </div>
}

type OrdersInnerProps = {
   brandHouseId?: string
   shopId?: string
}

const OrdersInner = ({brandHouseId, shopId}: OrdersInnerProps) => {
   const {data} = useOrderSettingsQuery({
      variables: {
         shopId: shopId,
         brandHouseId: brandHouseId
      },
      fetchPolicy: "no-cache"
   })
   const [saveOrders] = useOrderSettingsUpdateMutation()

   if (!data) {
      return <LinearProgress/>
   }

   const onSave = async (data: OrderSettingsFragment) => {
      if (!data.refundAdjustment?.oncePerOrder) {
         data.refundAdjustment!.oncePerOrder = false
      }
      // Parse deliveryNote back from JSON string to object
      if (typeof data.deliveryNote === 'string' && data.deliveryNote.trim()) {
         try {
            data.deliveryNote = JSON.parse(data.deliveryNote)
         } catch (error) {
            data.deliveryNote = {}
         }
      }
      await SavingUI.process(async () => {
         await saveOrders({
            variables: {
               shopId: shopId,
               brandHouseId: brandHouseId,
               input: data
            }
         });
      }, 'Order setttings updated')
   }

   const currencies = (data.BrandSettings?.stockItemConfig?.currencies || [])
      .filter(active => !!active!.active) as Currency[]
   const editData = (data!.BrandSettings?.order || {}) as OrderSettingsFragment

   // Convert deliveryNote to formatted JSON string if it's an object
   if (editData.deliveryNote && typeof editData.deliveryNote === 'object') {
      editData.deliveryNote = JSON.stringify(editData.deliveryNote, null, 2) as any
   } else if (typeof editData.deliveryNote === 'string') {
      // If it's already a string, try to parse and re-stringify for formatting
      try {
         const parsed = JSON.parse(editData.deliveryNote as any)
         editData.deliveryNote = JSON.stringify(parsed, null, 2) as any
      } catch (e) {
         // If parsing fails, leave it as is or set to empty object
         editData.deliveryNote = '{}' as any
      }
   } else {
      editData.deliveryNote = '{}' as any
   }

   for (const currency of currencies) {
      const adjustCur = editData.refundAdjustment?.adjust?.find(adj => adj?.currency === currency.currency);
      if (!adjustCur) {
         if (!editData.refundAdjustment) {
            editData.refundAdjustment = {
               oncePerOrder: true,
               reason: '',
               adjust: []
            }
         }
         if (!editData.refundAdjustment!.adjust) {
            editData.refundAdjustment!.adjust = []
         }
         editData.refundAdjustment!.adjust!.push({
            currency: currency.currency,
            value: 0
         })
      }
   }

   return <OrderSettingsEditor
      data={editData} onSave={onSave}/>
}


type OrderSettingsEditorProps = {
   data?: OrderSettingsFragment
   onSave: (data: OrderSettingsFragment) => Promise<void>
}

const OrderSettingsEditor = ({data, onSave}: OrderSettingsEditorProps) => {
   const methods = useForm<OrderSettingsFragment>({
      defaultValues: data
   });
   const {handleSubmit} = methods;

   return <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSave)} id={FORMID_ORDERS}/>
      <PageContent>
         <TitleText type={"h2"}>Returns</TitleText>
         <FormInputNumber name={"returnMaxDays"} label={"Return within days"}/>

         <TitleText type={"subtitle1"}>Refund amount deduction</TitleText>
         {data?.refundAdjustment?.adjust?.map((adjust, i) => {
            return <FormInputPrice name={`refundAdjustment.adjust.${i}.value`}
                                   label={"Refund amount (" + adjust!.currency + ")"}/>
         })}
         <FormInputText name={"refundAdjustment.reason"} label={"Reason for adjustment"}/>
         <FormInputCheckbox name={"refundAdjustment.oncePerOrder"} label={"Only adjust once per order"}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Delivery note</TitleText>
         <FormInputJSON name={"deliveryNote"}
                        height={"200px"}
                        placeholder={"Delivery note configuration"}
         />
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>VAT</TitleText>
         <FormInputText name={"vatRate"} label={"VAT Rate"}/>
         <FormInputText name={"gbEori"} label={"GB-EORI Number (For exports to GB)"}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Warehouse Management System</TitleText>
         <FormInputCheckbox name={"externalWms"} label={"External WMS"}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Warehouse</TitleText>
         <FormInputCheckbox name={"externalWarehouse"} label={"External Warehouse"}/>
      </PageContent>
   </FormProvider>
}